import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { uniqBy, capitalize } from 'lodash-es';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { NAVIGATION_MENU_HEIGHT } from 'site-modules/comparator/constants/comparator';
import { NO_STYLE_ID } from 'site-modules/comparator/constants/trims-comparator';
import { getFeatureValue } from 'site-modules/shared/utils/features-specs/utils';
import { comparatorGridStickyScroll } from 'site-modules/comparator/components/comparator-grid-sticky-scroll/comparator-grid-sticky-scroll';
import { TrimsComparatorTable } from 'site-modules/comparator/components/trims-comparator-table/trims-comparator-table';

export function TrimsComparatorGridUI({
  vehicleParams,
  styles,
  title,
  navList,
  onStickyStateChange,
  comparatorHeaderRef,
  comparatorHeaderStickyRef,
  setCustomOffset,
  customOffset = 0,
  arrows,
  onArrowClick,
  isMobile = false,
}) {
  const [columns, setColumns] = useState(
    uniqBy(styles, 'trim').map(({ id, trim }, index) => ({
      name: `veh${index + 1}`,
      trim,
      styleId: id,
      driveTrainOptions: uniqBy(
        styles
          .filter(({ trim: driveTrainTrim }) => driveTrainTrim === trim)
          .map(({ id: driveTrainStyleId, features }) => ({
            trim,
            id: driveTrainStyleId,
            driveTrain: capitalize(getFeatureValue(features, 'standard["Drive Train"]', 'Drive type')),
          })),
        'driveTrain'
      ),
    }))
  );
  const [removedTrims, setRemovedTrims] = useState([]);

  const setTrimStyle = useCallback(
    ({ id, trim }) => {
      setColumns(columns.map(column => (column.trim === trim ? { ...column, styleId: id } : column)));
    },
    [columns]
  );

  const onRemoveTrim = useCallback(
    trim => {
      setRemovedTrims([...removedTrims, trim]);
    },
    [removedTrims]
  );

  const onAddTrim = useCallback(
    trim => {
      setRemovedTrims(removedTrims.filter(removedTrim => removedTrim !== trim));
    },
    [removedTrims]
  );

  const currentColumns = useMemo(
    () =>
      removedTrims?.length
        ? columns.filter(({ trim }) => !removedTrims.includes(trim)).concat({ name: 'veh0', styleId: NO_STYLE_ID })
        : columns,

    [columns, removedTrims]
  );

  const currentStyles = useMemo(
    () => currentColumns.map(({ styleId }) => styles.find(({ id }) => id === styleId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentColumns]
  );

  const columnStyleIds = currentColumns.map(({ styleId }) => styleId).join();
  useEffect(() => {
    if (comparatorHeaderStickyRef.current) {
      setCustomOffset(comparatorHeaderStickyRef.current.clientHeight + NAVIGATION_MENU_HEIGHT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnStyleIds]);

  return (
    <div className="trims-comparator-grid">
      <TrimsComparatorTable
        vehicleParams={vehicleParams}
        columns={currentColumns}
        styles={currentStyles}
        removedTrims={removedTrims}
        setTrimStyle={setTrimStyle}
        onAddTrim={onAddTrim}
        onRemoveTrim={onRemoveTrim}
        navList={navList}
        customOffset={customOffset}
        onStickyStateChange={onStickyStateChange}
        title={title}
        comparatorHeaderRef={comparatorHeaderRef}
        comparatorHeaderStickyRef={comparatorHeaderStickyRef}
        arrows={arrows}
        onArrowClick={onArrowClick}
        isSmall={isMobile}
      />
    </div>
  );
}

TrimsComparatorGridUI.propTypes = {
  onStickyStateChange: PropTypes.func.isRequired,
  setCustomOffset: PropTypes.func.isRequired,
  comparatorHeaderRef: PropTypes.shape().isRequired,
  comparatorHeaderStickyRef: PropTypes.shape().isRequired,
  title: PropTypes.string.isRequired,
  styles: PropTypes.arrayOf(VehicleEntities.Style).isRequired,
  navList: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hash: PropTypes.string })).isRequired,
  arrows: PropTypes.shape({ left: PropTypes.bool, right: PropTypes.bool }).isRequired,
  onArrowClick: PropTypes.func.isRequired,
  vehicleParams: CorePageParams.isRequired,
  customOffset: PropTypes.number,
  isMobile: PropTypes.bool,
};

export const TrimsComparatorGrid = comparatorGridStickyScroll(TrimsComparatorGridUI, { withArrows: true });
